/**
 * score's permission manifest (AUTHZ.md §5.2). This module **imports nothing**
 * — the package's `exports` map exposes it as the isolated `score-common/permissions`
 * subpath so importing it never drags in the heavy generated axios client. Both
 * score-api (its own enforcement) and the discrete `permissions` aggregator pull
 * the manifest with zero client bloat.
 *
 * Keys are authored short (no `score:` prefix); the app name is implied by the
 * manifest. The canonical/wire form is `score:<key>` (token, audit log, account
 * admin UI). Roles bundle permissions flatly — no inheritance, no negatives
 * (§5.1): a role that should "include" another copies its permissions in.
 *
 * The object-map shape (keyed by short key) lets ESLint `no-dupe-keys` flag
 * in-file collisions for free; cross-file dupes + role→permission integrity are
 * caught by the aggregator's validator.
 */
export const manifest = {
  app: "score",
  permissions: {
    //  short key            title (UI)              description (UI)                                     group
    list_shows_org: {
      title: "List Shows",
      description: "List all shows for the organization",
      group: "Shows",
    },
    list_shows_mine: {
      title: "List My Shows",
      description: "List only shows you are assigned to",
      group: "Shows",
    },
    edit_score_sheet: {
      title: "Edit Score Sheet",
      description: "Edit scores on an existing score sheet",
      group: "Scoring",
    },
    sign_score_sheet: {
      title: "Sign Score Sheet",
      description: "Apply a judge signature to a score sheet",
      group: "Scoring",
    },
    assign_scribes: {
      title: "Assign Scribes",
      description: "Assign scribes to rings and classes for a show",
      group: "Administration",
    },
  },
  roles: {
    score_admin: {
      title: "Score Admin",
      description: "Administer scoring for the organization, including scribe assignment",
      permissions: ["list_shows_org", "assign_scribes"],
    },
    score_scribe: {
      title: "Scribe",
      description: "Enter and update scores during a show",
      permissions: ["list_shows_org", "edit_score_sheet"],
    },
    score_judge: {
      title: "Judge",
      description: "Score and sign sheets for shows you are assigned to",
      // Flat, fully-resolved set — no runtime inheritance (§5.1).
      permissions: ["list_shows_mine", "edit_score_sheet", "sign_score_sheet"],
    },
    score_viewer: {
      title: "Viewer",
      description: "Read-only access to shows",
      permissions: ["list_shows_mine"],
    },
    score_announcer: {
      title: "Announcer",
      description: "Read-only access to the whole show, for ring announcing",
      // list_shows_org (not list_shows_mine, cf. score_viewer above) — an
      // announcer needs the entire show, not just shows they're personally
      // assigned to.
      permissions: ["list_shows_org"],
    },
  },
};

export default manifest;
